import React, { useState, useEffect, useContext } from 'react';
import { Card, Table, Spinner, Alert, Tabs, Tab, Badge, Button, Row, Col, Modal } from 'react-bootstrap';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { FaUser, FaFileAlt, FaCalendar, FaReceipt, FaDownload, FaEye, FaHeartbeat, FaPhone, FaMapMarkerAlt, FaPrint, FaTicketAlt } from 'react-icons/fa';
import './PatientPortal.css';

const PatientPortal = () => {
  const { user, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [patientData, setPatientData] = useState(null);
  const [reports, setReports] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [bills, setBills] = useState([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [clinic, setClinic] = useState(null);
  const [stats, setStats] = useState({
    totalReports: 0,
    totalAppointments: 0,
    pendingBills: 0,
    upcomingAppointments: 0
  });


  useEffect(() => {
    fetchPatientData();
    fetchClinicData();
  }, []);

  const fetchClinicData = async () => {
    try {
      const res = await axios.get('/api/clinic.php');
      setClinic(res.data);
    } catch (err) {
      console.error('Error fetching clinic data:', err);
    }
  };

  const fetchPatientData = async () => {
    try {
      const patientRes = await axios.get(`/api/patients/${user.id}`);
      setPatientData(patientRes.data);

      const reportsRes = await axios.get(`/api/reports/patient/${user.id}`);
      setReports(reportsRes.data);

      try {
        const appointmentsRes = await axios.get(`/api/appointments/patient/${user.id}`);
        setAppointments(appointmentsRes.data);
      } catch (err) {
        console.log('Appointments not available');
        setAppointments([]);
      }

      try {
        const billsRes = await axios.get(`/api/billings.php?patientId=${user.id}`);
        setBills(billsRes.data);
      } catch (err) {
        console.log('Bills not available');
        setBills([]);
      }

      const upcomingAppts = appointments.filter(apt => 
        new Date(apt.appointmentDate) >= new Date() && apt.status !== 'cancelled'
      ).length;

      const pendingBills = bills.filter(bill => bill.paymentStatus !== 'paid').length;

      setStats({
        totalReports: reportsRes.data.length,
        totalAppointments: appointments.length,
        pendingBills: pendingBills,
        upcomingAppointments: upcomingAppts
      });

      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const handleViewReport = async (reportId) => {
    setSelectedReport({ id: reportId });
    setShowReportModal(true);
  };



  const handleDownloadReport = (reportId) => {
    // Open same report view as View Details
    window.open(`/view-report/${reportId}`, '_blank');
  };

  const handleViewTicket = (appointment) => {
    setSelectedAppointment(appointment);
    setShowTicketModal(true);
  };

  const handlePrintTicket = () => {
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Appointment Ticket</title>');
    printWindow.document.write('<style>body{font-family:Arial;padding:20px;}</style>');
    printWindow.document.write('</head><body>');
    printWindow.document.write(document.getElementById('ticket-print-area').innerHTML);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const handleViewBill = (bill) => {
    // Parse billItems if it's a string
    if (bill.billItems && typeof bill.billItems === 'string') {
      try {
        bill.billItems = JSON.parse(bill.billItems);
      } catch (e) {
        console.error('Error parsing billItems:', e);
        bill.billItems = [];
      }
    }
    
    // Ensure billItems is an array
    if (!Array.isArray(bill.billItems)) {
      bill.billItems = [];
    }
    
    setSelectedBill(bill);
    setShowBillModal(true);
  };

  const handlePrintBill = () => {
    if (!selectedBill) return;
    
    const printWindow = window.open('', '', 'height=600,width=800');
    const balance = selectedBill.totalAmount - selectedBill.paidAmount;
    const subtotal = selectedBill.billItems?.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0) || 0;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Bill ${selectedBill.billNumber}</title>
        <style>
          @page { size: A4; margin: 15mm; }
          body { font-family: Arial, sans-serif; padding: 20px; margin: 0; }
          .container { max-width: 800px; margin: 0 auto; position: relative; }
          .header { text-align: center; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 1px solid #ddd; }
          .bill-info { display: flex; justify-content: space-between; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin: 15px 0; }
          th, td { padding: 8px; text-align: left; border: 1px solid #ddd; }
          th { background-color: #f5f5f5; font-weight: bold; }
          .text-end { text-align: right; }
          .text-center { text-align: center; }
          .fw-bold { font-weight: bold; }
          .footer { margin-top: 60px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; }
          .paid-stamp {
            position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-30deg);
            font-size: 6rem; font-weight: bold; color: rgba(76, 175, 80, 0.3);
            border: 0.5rem solid rgba(76, 175, 80, 0.3); padding: 1rem 2rem; border-radius: 1rem;
          }
        </style>
      </head>
      <body>
        <div class="container">
          ${selectedBill.paymentStatus === 'complete' ? '<div class="paid-stamp">PAID</div>' : ''}
          
          <div class="header">
            ${clinic && clinic.logo ? `<img src="${clinic.logo}" alt="Clinic Logo" style="max-height: 80px; max-width: 200px; margin-bottom: 10px;">` : ''}
            <h2>Invoice</h2>
            <h3>${clinic?.name || 'Doctero Clinic'}</h3>
            ${clinic?.address ? `<p style="margin: 0; font-size: 10pt; color: #666;">${clinic.address}</p>` : ''}
            ${clinic?.contactNumber ? `<p style="margin: 0; font-size: 10pt; color: #666;">Contact: ${clinic.contactNumber}</p>` : ''}
          </div>
          
          <div class="bill-info">
            <div style="width: 48%;">
              <h5>Bill To:</h5>
              <p><strong>Patient:</strong> ${selectedBill.Patient?.name || 'N/A'}</p>
              <p><strong>ID:</strong> ${selectedBill.Patient?.patientId || 'N/A'}</p>
              <p><strong>Contact:</strong> ${selectedBill.Patient?.contactNumber || 'N/A'}</p>
            </div>
            <div style="width: 48%; text-align: right;">
              <h5>Bill Details:</h5>
              <p><strong>Bill #:</strong> ${selectedBill.billNumber}</p>
              <p><strong>Date:</strong> ${new Date(selectedBill.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              <p><strong>Status:</strong> ${selectedBill.paymentStatus}</p>
              ${selectedBill.Doctor ? `<p><strong>Doctor:</strong> ${selectedBill.Doctor.name}</p>` : ''}
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th style="width: 50%;">Description</th>
                <th style="width: 15%;" class="text-center">Quantity</th>
                <th style="width: 15%;" class="text-center">Unit Price</th>
                <th style="width: 20%;" class="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${selectedBill.billItems && selectedBill.billItems.length > 0 ? 
                selectedBill.billItems.map(item => `
                  <tr>
                    <td>${item.description}</td>
                    <td class="text-center">${item.quantity}</td>
                    <td class="text-center">Rs ${item.unitPrice?.toLocaleString()}</td>
                    <td class="text-end">Rs ${(item.amount || (item.quantity * item.unitPrice))?.toLocaleString()}</td>
                  </tr>
                `).join('') : 
                '<tr><td colspan="4" class="text-center">No items found</td></tr>'
              }
            </tbody>
            <tfoot>
              <tr>
                <td colspan="3" class="text-end fw-bold">Subtotal:</td>
                <td class="text-end fw-bold">Rs ${subtotal.toLocaleString()}</td>
              </tr>
              ${selectedBill.discount > 0 ? `
              <tr>
                <td colspan="3" class="text-end">Discount (${selectedBill.discount}%):</td>
                <td class="text-end" style="color: #dc3545;">-Rs ${(subtotal * selectedBill.discount / 100).toLocaleString()}</td>
              </tr>
              ` : ''}
              ${selectedBill.tax > 0 ? `
              <tr>
                <td colspan="3" class="text-end">Tax (${selectedBill.tax}%):</td>
                <td class="text-end">Rs ${(subtotal * (1 - selectedBill.discount / 100) * selectedBill.tax / 100).toLocaleString()}</td>
              </tr>
              ` : ''}
              <tr>
                <td colspan="3" class="text-end fw-bold">Total:</td>
                <td class="text-end fw-bold">Rs ${selectedBill.totalAmount?.toLocaleString()}</td>
              </tr>
              <tr>
                <td colspan="3" class="text-end fw-bold">Amount Paid:</td>
                <td class="text-end fw-bold">Rs ${selectedBill.paidAmount?.toLocaleString()}</td>
              </tr>
              <tr>
                <td colspan="3" class="text-end fw-bold">Balance Due:</td>
                <td class="text-end fw-bold" style="color: ${balance > 0 ? '#dc3545' : '#28a745'};">Rs ${balance.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
          
          ${selectedBill.notes ? `
            <div style="margin-top: 20px;">
              <h6>Notes:</h6>
              <p>${selectedBill.notes}</p>
            </div>
          ` : ''}
          
          <div class="footer">
            <p>Thank you for choosing our services.</p>
          </div>
        </div>
        
        <script>
          window.onload = function() {
            setTimeout(function() { window.print(); }, 500);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="patient-portal-loading">
        <Spinner animation="border" variant="primary" />
        <p>Loading your health records...</p>
      </div>
    );
  }

  return (
    <div className="patient-portal">
      {/* Header */}
      <div className="patient-portal-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <div className="clinic-logo" style={{ background: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {clinic && clinic.logo ? (
                  <img src={clinic.logo} alt="Clinic Logo" style={{ maxHeight: '60px', maxWidth: '60px', objectFit: 'contain' }} />
                ) : (
                  <FaHeartbeat />
                )}
              </div>
              <div className="header-info">
                <h1>{clinic?.name || 'Doctero Clinic'}</h1>
                <p>Patient Portal</p>
              </div>
            </div>
            <div className="header-right">
              <div className="patient-info-header">
                <div className="patient-avatar">
                  {patientData?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4>{patientData?.name}</h4>
                  <p>ID: {patientData?.patientId}</p>
                </div>
              </div>
              <Button variant="outline-light" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container patient-portal-content">
        {/* Welcome Section */}
        <div className="welcome-section">
          <h2>Welcome back, {patientData?.name}!</h2>
          <p>Here's your health summary</p>
        </div>

        {/* Stats Cards */}
        <Row className="stats-row">
          <Col md={3}>
            <Card className="stat-card stat-card-blue">
              <Card.Body>
                <div className="stat-icon">
                  <FaFileAlt />
                </div>
                <div className="stat-info">
                  <h3>{stats.totalReports}</h3>
                  <p>Medical Reports</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card stat-card-green">
              <Card.Body>
                <div className="stat-icon">
                  <FaCalendar />
                </div>
                <div className="stat-info">
                  <h3>{stats.upcomingAppointments}</h3>
                  <p>Upcoming Appointments</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card stat-card-orange">
              <Card.Body>
                <div className="stat-icon">
                  <FaReceipt />
                </div>
                <div className="stat-info">
                  <h3>{stats.pendingBills}</h3>
                  <p>Pending Bills</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card stat-card-purple">
              <Card.Body>
                <div className="stat-icon">
                  <FaUser />
                </div>
                <div className="stat-info">
                  <h3>{patientData?.age}</h3>
                  <p>Years Old</p>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Personal Information Card */}
        <Card className="info-card mb-4">
          <Card.Header>
            <FaUser className="me-2" /> Personal Information
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="info-item">
                  <label>Full Name</label>
                  <p>{patientData?.name}</p>
                </div>
                <div className="info-item">
                  <label>Patient ID</label>
                  <p className="patient-id-badge">{patientData?.patientId}</p>
                </div>
                <div className="info-item">
                  <label>Age</label>
                  <p>{patientData?.age} years</p>
                </div>
              </Col>
              <Col md={6}>
                <div className="info-item">
                  <label>Gender</label>
                  <p>{patientData?.gender}</p>
                </div>
                <div className="info-item">
                  <label>Blood Group</label>
                  <p><Badge bg="danger">{patientData?.bloodGroup || 'Not specified'}</Badge></p>
                </div>
                <div className="info-item">
                  <label>Contact Number</label>
                  <p><FaPhone className="me-2" />{patientData?.contactNumber}</p>
                </div>
              </Col>
            </Row>
            {patientData?.address && (
              <div className="info-item">
                <label>Address</label>
                <p><FaMapMarkerAlt className="me-2" />{patientData.address}</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Tabs Section */}
        <Tabs defaultActiveKey="reports" className="portal-tabs">
          {/* Medical Reports Tab */}
          <Tab eventKey="reports" title={
            <span><FaFileAlt className="me-2" />Medical Reports ({reports.length})</span>
          }>
            <Card className="tab-content-card">
              <Card.Body>
                {reports.length > 0 ? (
                  <div className="reports-grid">
                    {reports.map(report => (
                      <Card key={report.id} className="report-card">
                        <Card.Body>
                          <div className="report-header">
                            <div className="report-icon">
                              <FaFileAlt />
                            </div>
                            <div className="report-info">
                              <h5>Medical Report</h5>
                              <p className="report-date">
                                {new Date(report.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="report-details">
                            <div className="detail-row">
                              <span className="detail-label">Doctor:</span>
                              <span className="detail-value">Dr. {report.Doctor?.name || 'N/A'}</span>
                            </div>
                            <div className="detail-row">
                              <span className="detail-label">Diagnosis:</span>
                              <span className="detail-value">{report.diagnosis?.substring(0, 50)}...</span>
                            </div>
                          </div>
                          <div className="report-actions">
                            <Button variant="primary" size="sm" onClick={() => handleViewReport(report.id)}>
                              <FaEye className="me-1" /> View Details
                            </Button>
                          </div>
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info" className="empty-state">
                    <FaFileAlt size={40} />
                    <h5>No Medical Reports</h5>
                    <p>You don't have any medical reports yet</p>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Appointments Tab */}
          <Tab eventKey="appointments" title={
            <span><FaCalendar className="me-2" />Appointments ({appointments.length})</span>
          }>
            <Card className="tab-content-card">
              <Card.Body>
                {appointments.length > 0 ? (
                  <Table responsive hover className="appointments-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Doctor</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {appointments.map(apt => (
                        <tr key={apt.id}>
                          <td>
                            <strong>{new Date(apt.appointmentDate).toLocaleDateString()}</strong>
                          </td>
                          <td>{apt.appointmentTime}</td>
                          <td>
                            <div className="doctor-info">
                              <div className="doctor-avatar-small">
                                {apt.Doctor?.name?.charAt(0)}
                              </div>
                              <span>Dr. {apt.Doctor?.name || 'N/A'}</span>
                            </div>
                          </td>
                          <td>
                            <Badge bg={
                              apt.status === 'completed' ? 'success' : 
                              apt.status === 'cancelled' ? 'danger' : 
                              apt.status === 'confirmed' ? 'primary' : 'warning'
                            }>
                              {apt.status}
                            </Badge>
                          </td>
                          <td>
                            <Button 
                              variant="primary" 
                              size="sm"
                              onClick={() => handleViewTicket(apt)}
                            >
                              <FaTicketAlt className="me-1" /> Ticket
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Alert variant="info" className="empty-state">
                    <FaCalendar size={40} />
                    <h5>No Appointments</h5>
                    <p>You don't have any appointments scheduled</p>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Bills Tab */}
          <Tab eventKey="bills" title={
            <span><FaReceipt className="me-2" />Bills & Payments ({bills.length})</span>
          }>
            <Card className="tab-content-card">
              <Card.Body>
                {bills.length > 0 ? (
                  <div className="bills-list">
                    {bills.map(bill => (
                      <Card key={bill.id} className="bill-card">
                        <Card.Body>
                          <Row>
                            <Col md={8}>
                              <div className="bill-info">
                                <h5>Bill #{bill.billNumber}</h5>
                                <p className="bill-date">
                                  Date: {new Date(bill.createdAt).toLocaleDateString()}
                                </p>
                                <div className="bill-details-row">
                                  <span>Services: {bill.services?.length || 0} items</span>
                                </div>
                              </div>
                            </Col>
                            <Col md={4} className="text-end">
                              <div className="bill-amount">
                                <h3>Rs {bill.totalAmount?.toLocaleString()}</h3>
                                <Badge bg={bill.paymentStatus === 'paid' ? 'success' : 'warning'}>
                                  {bill.paymentStatus}
                                </Badge>
                              </div>
                              <div className="bill-actions mt-3">
                                <Button variant="primary" size="sm" className="me-2" onClick={() => handleViewBill(bill)}>
                                  <FaEye className="me-1" /> View
                                </Button>
                                <Button variant="outline-secondary" size="sm" onClick={() => { handleViewBill(bill); setTimeout(handlePrintBill, 500); }}>
                                  <FaPrint className="me-1" /> Print
                                </Button>
                              </div>
                            </Col>
                          </Row>
                          {bill.paymentStatus !== 'complete' && bill.paymentStatus !== 'paid' && (
                            <div className="payment-reminder">
                              <Alert variant="warning" className="mb-0 mt-3">
                                Payment pending. Please contact clinic for payment.
                              </Alert>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info" className="empty-state">
                    <FaReceipt size={40} />
                    <h5>No Bills</h5>
                    <p>You don't have any bills yet</p>
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Tab>

          {/* Health Summary Tab */}
          <Tab eventKey="health" title={
            <span><FaHeartbeat className="me-2" />Health Summary</span>
          }>
            <Card className="tab-content-card">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Card className="health-card">
                      <Card.Body>
                        <h5>Medical History</h5>
                        <div className="health-info">
                          {patientData?.history ? (
                            <p>{patientData.history}</p>
                          ) : (
                            <p className="text-muted">No medical history recorded</p>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="health-card">
                      <Card.Body>
                        <h5>Recent Visits</h5>
                        <div className="health-info">
                          <p>Total Reports: <strong>{reports.length}</strong></p>
                          <p>Total Appointments: <strong>{appointments.length}</strong></p>
                          {reports.length > 0 && (
                            <p>Last Visit: <strong>
                              {new Date(reports[0].createdAt).toLocaleDateString()}
                            </strong></p>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>

        {/* Contact Clinic Section */}
        <Card className="contact-clinic-card mt-4">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={8}>
                <h4>Need Help?</h4>
                <p>Contact our clinic for appointments, queries, or emergencies</p>
              </Col>
              <Col md={4} className="text-end">
                <Button variant="primary" size="lg" onClick={() => setShowContactModal(true)}>
                  <FaPhone className="me-2" /> Contact Clinic
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      {/* Footer */}
      <div className="patient-portal-footer">
        <div className="container">
          <p>&copy; 2026 MEDX ARENA. All rights reserved. | Developer: Abdullah Arif | +92-3137323866</p>
        </div>
      </div>

      {/* Ticket Modal */}
      <Modal show={showTicketModal} onHide={() => setShowTicketModal(false)} size="md" centered className="no-print">
        <Modal.Header closeButton className="no-print">
          <Modal.Title>Appointment Ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAppointment && (
            <div id="ticket-print-area" style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
              <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '2px solid #000', paddingBottom: '15px' }}>
                <h3 style={{ margin: '0 0 10px 0' }}>Doctero Clinic</h3>
                <p style={{ margin: '5px 0', fontSize: '14px' }}>Appointment Ticket</p>
              </div>
              
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ddd' }}>
                  <strong>Ticket No:</strong>
                  <span>APT-{selectedAppointment.id}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ddd' }}>
                  <strong>Patient:</strong>
                  <span>{selectedAppointment.Patient?.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ddd' }}>
                  <strong>Doctor:</strong>
                  <span>Dr. {selectedAppointment.Doctor?.name}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ddd' }}>
                  <strong>Date:</strong>
                  <span>{new Date(selectedAppointment.appointmentDate).toLocaleDateString('en-GB')}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ddd' }}>
                  <strong>Time:</strong>
                  <span>{(() => {
                    const [hours, minutes] = selectedAppointment.appointmentTime.split(':');
                    const hour = parseInt(hours);
                    const ampm = hour >= 12 ? 'PM' : 'AM';
                    const formattedHour = hour % 12 || 12;
                    return `${formattedHour}:${minutes} ${ampm}`;
                  })()}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ddd' }}>
                  <strong>Status:</strong>
                  <Badge bg={selectedAppointment.status === 'completed' ? 'success' : 'primary'}>
                    {selectedAppointment.status}
                  </Badge>
                </div>
              </div>
              
              <div style={{ textAlign: 'center', marginTop: '20px', paddingTop: '15px', borderTop: '2px solid #000' }}>
                <p style={{ margin: '5px 0' }}>Thank You For Your Visit!</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="no-print">
          <Button variant="secondary" onClick={() => setShowTicketModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handlePrintTicket}>
            <FaPrint className="me-1" /> Print
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bill Modal */}
      <Modal show={showBillModal} onHide={() => setShowBillModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Bill Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBill && (
            <div id="bill-print-area">
              <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', backgroundColor: '#fff', position: 'relative' }}>
                {selectedBill.paymentStatus === 'complete' && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-30deg)',
                    fontSize: '6rem',
                    fontWeight: 'bold',
                    color: 'rgba(76, 175, 80, 0.3)',
                    border: '0.5rem solid rgba(76, 175, 80, 0.3)',
                    padding: '1rem 2rem',
                    borderRadius: '1rem',
                    pointerEvents: 'none',
                    zIndex: 100
                  }}>PAID</div>
                )}
                
                <div style={{ textAlign: 'center', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #ddd' }}>
                  {clinic && clinic.logo && (
                    <img src={clinic.logo} alt="Clinic Logo" style={{ maxHeight: '80px', maxWidth: '200px', marginBottom: '10px' }} />
                  )}
                  <h2 style={{ margin: '0 0 10px 0' }}>Invoice</h2>
                  <h3 style={{ margin: '0 0 5px 0' }}>{clinic?.name || 'Doctero Clinic'}</h3>
                  {clinic?.address && <p style={{ margin: '0', fontSize: '10pt', color: '#666' }}>{clinic.address}</p>}
                  {clinic?.contactNumber && <p style={{ margin: '0', fontSize: '10pt', color: '#666' }}>Contact: {clinic.contactNumber}</p>}
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <div style={{ width: '48%' }}>
                    <h5 style={{ marginBottom: '10px' }}>Bill To:</h5>
                    <p style={{ margin: '5px 0' }}><strong>Patient:</strong> {selectedBill.Patient?.name || 'N/A'}</p>
                    <p style={{ margin: '5px 0' }}><strong>ID:</strong> {selectedBill.Patient?.patientId || 'N/A'}</p>
                    <p style={{ margin: '5px 0' }}><strong>Contact:</strong> {selectedBill.Patient?.contactNumber || 'N/A'}</p>
                  </div>
                  <div style={{ width: '48%', textAlign: 'right' }}>
                    <h5 style={{ marginBottom: '10px' }}>Bill Details:</h5>
                    <p style={{ margin: '5px 0' }}><strong>Bill #:</strong> {selectedBill.billNumber}</p>
                    <p style={{ margin: '5px 0' }}><strong>Date:</strong> {new Date(selectedBill.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    <p style={{ margin: '5px 0' }}><strong>Status:</strong> <Badge bg={selectedBill.paymentStatus === 'complete' ? 'success' : selectedBill.paymentStatus === 'partial' ? 'info' : 'warning'}>{selectedBill.paymentStatus}</Badge></p>
                    {selectedBill.Doctor && <p style={{ margin: '5px 0' }}><strong>Doctor:</strong> {selectedBill.Doctor.name}</p>}
                  </div>
                </div>
                
                <table style={{ width: '100%', borderCollapse: 'collapse', margin: '15px 0' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '8px', textAlign: 'left', border: '1px solid #ddd', width: '50%' }}>Description</th>
                      <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', width: '15%' }}>Quantity</th>
                      <th style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd', width: '15%' }}>Unit Price</th>
                      <th style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', width: '20%' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBill.billItems && selectedBill.billItems.length > 0 ? (
                      selectedBill.billItems.map((item, index) => (
                        <tr key={index}>
                          <td style={{ padding: '8px', border: '1px solid #ddd' }}>{item.description}</td>
                          <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>{item.quantity}</td>
                          <td style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>Rs {item.unitPrice?.toLocaleString()}</td>
                          <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>Rs {(item.amount || (item.quantity * item.unitPrice))?.toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ padding: '8px', textAlign: 'center', border: '1px solid #ddd' }}>No items found in this bill</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <td colSpan="3" style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', fontWeight: 'bold' }}>Subtotal:</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', fontWeight: 'bold' }}>
                        Rs {selectedBill.billItems?.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0).toLocaleString()}
                      </td>
                    </tr>
                    {selectedBill.discount > 0 && (
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <td colSpan="3" style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>Discount ({selectedBill.discount}%):</td>
                        <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', color: '#dc3545' }}>
                          -Rs {((selectedBill.billItems?.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0) * selectedBill.discount / 100) || 0).toLocaleString()}
                        </td>
                      </tr>
                    )}
                    {selectedBill.tax > 0 && (
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <td colSpan="3" style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>Tax ({selectedBill.tax}%):</td>
                        <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd' }}>
                          Rs {((selectedBill.billItems?.reduce((sum, item) => sum + (item.amount || (item.quantity * item.unitPrice) || 0), 0) * (1 - selectedBill.discount / 100) * selectedBill.tax / 100) || 0).toLocaleString()}
                        </td>
                      </tr>
                    )}
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <td colSpan="3" style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', fontWeight: 'bold' }}>Total:</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', fontWeight: 'bold' }}>Rs {selectedBill.totalAmount?.toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td colSpan="3" style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', fontWeight: 'bold' }}>Amount Paid:</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', fontWeight: 'bold' }}>Rs {selectedBill.paidAmount?.toLocaleString()}</td>
                    </tr>
                    <tr>
                      <td colSpan="3" style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', fontWeight: 'bold' }}>Balance Due:</td>
                      <td style={{ padding: '8px', textAlign: 'right', border: '1px solid #ddd', fontWeight: 'bold', color: (selectedBill.totalAmount - selectedBill.paidAmount) > 0 ? '#dc3545' : '#28a745' }}>
                        Rs {(selectedBill.totalAmount - selectedBill.paidAmount)?.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
                
                {selectedBill.notes && (
                  <div style={{ marginTop: '20px' }}>
                    <h6 style={{ marginBottom: '5px' }}>Notes:</h6>
                    <p>{selectedBill.notes}</p>
                  </div>
                )}
                
                <div style={{ marginTop: '60px', paddingTop: '20px', borderTop: '1px solid #ddd', textAlign: 'center' }}>
                  <p>Thank you for choosing our services.</p>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBillModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handlePrintBill}>
            <FaPrint className="me-1" /> Print
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Report Modal */}
      <Modal show={showReportModal} onHide={() => setShowReportModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Medical Report</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: '80vh' }}>
          {selectedReport && (
            <iframe 
              src={`/view-report/${selectedReport.id}?hideBack=true`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Medical Report"
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReportModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Contact Clinic Modal */}
      <Modal show={showContactModal} onHide={() => setShowContactModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Contact Clinic</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ padding: '10px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h5><FaPhone className="me-2" />Phone Number</h5>
              <p style={{ fontSize: '18px', fontWeight: 'bold' }}>{clinic?.contactNumber || 'Not available'}</p>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <h5>Email</h5>
              <p style={{ fontSize: '18px', fontWeight: 'bold' }}>{clinic?.email || 'Not available'}</p>
            </div>
            {clinic?.address && (
              <div>
                <h5><FaMapMarkerAlt className="me-2" />Address</h5>
                <p>{clinic.address}</p>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowContactModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default PatientPortal;
